import type { RelationshipPair } from '../../../core/entities/graph-document';
import type { RelationshipDisplaySummary } from '../../../core/policies/explicit-relationships';
import type { JourneyNode } from '../../../core/entities/node';
import { useLocale } from '../../preferences/i18n/LocaleProvider';

export function RelationshipDetailSheet({
  sourceNode,
  targetNode,
  pair,
  onClose,
  onEdit,
  readonly = false,
  summary,
  derivedReasonSummaries = [],
}: {
  sourceNode: JourneyNode;
  targetNode?: JourneyNode | null;
  pair?: RelationshipPair | null;
  onClose: () => void;
  onEdit?(): void;
  readonly?: boolean;
  summary?: RelationshipDisplaySummary | null;
  derivedReasonSummaries?: string[];
}) {
  const { t } = useLocale();

  if (!pair || !targetNode) {
    return (
      <aside aria-label={t('relationshipDetailTitle')} className="floating-panel" style={{ width: 'min(480px, 90vw)', padding: 20 }}>
        <div style={{ display: 'grid', gap: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <h2 style={{ margin: 0, fontSize: 24 }}>{t('relationshipDetailTitle')}</h2>
            <button className="hud-subtle-button" onClick={onClose} type="button">
              {t('close')}
            </button>
          </div>
          <p style={{ margin: 0, color: 'rgba(239, 246, 255, 0.72)' }}>{t('relationshipDetailEmpty')}</p>
        </div>
      </aside>
    );
  }

  return (
    <aside aria-label={t('relationshipDetailTitle')} className="floating-panel" style={{ width: 'min(480px, 90vw)', padding: 20 }}>
      <div style={{ display: 'grid', gap: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div style={{ display: 'grid', gap: 6 }}>
            <span style={{ fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(180, 223, 255, 0.84)' }}>
              {t('relationshipDetailEyebrow')}
            </span>
            <h2 style={{ margin: 0, fontSize: 24 }}>{sourceNode.displayName}</h2>
          </div>
          <div className="hud-toolbar" style={{ justifyContent: 'flex-end' }}>
            {onEdit ? (
              <button className="hud-subtle-button" disabled={readonly} onClick={onEdit} type="button">
                {t('relationshipDetailEditAction')}
              </button>
            ) : null}
            <button className="hud-subtle-button" onClick={onClose} type="button">
              {t('close')}
            </button>
          </div>
        </div>

        {summary?.hasOverflow ? (
          <p style={{ margin: 0, color: 'rgba(180, 223, 255, 0.78)' }}>{t('relationshipSummaryOverflowHint')}</p>
        ) : null}
        {summary?.edgeKind === 'mixed' ? (
          <p style={{ margin: 0, color: 'rgba(255, 226, 181, 0.84)' }}>{t('relationshipMixedSummaryHint')}</p>
        ) : null}
        {readonly ? (
          <p style={{ margin: 0, color: 'rgba(255, 214, 143, 0.92)' }}>{t('relationshipReadonlyNotice')}</p>
        ) : null}

        <RelationshipDirectionSummary
          labels={findLabels(pair, sourceNode.nodeId, targetNode.nodeId)}
          title={`${sourceNode.displayName} -> ${targetNode.displayName}`}
          emptyLabel={t('relationshipDirectionNoLabels')}
        />
        <RelationshipDirectionSummary
          labels={findLabels(pair, targetNode.nodeId, sourceNode.nodeId)}
          title={`${targetNode.displayName} -> ${sourceNode.displayName}`}
          emptyLabel={t('relationshipDirectionNoLabels')}
        />
        {derivedReasonSummaries.length > 0 ? (
          <section style={{ display: 'grid', gap: 8, padding: 14, borderRadius: 18, background: 'rgba(32, 18, 4, 0.42)', border: '1px solid rgba(255, 214, 143, 0.14)' }}>
            <h3 style={{ margin: 0, fontSize: 14 }}>{t('relationshipDerivedReasonsTitle')}</h3>
            <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 6 }}>
              {derivedReasonSummaries.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>
    </aside>
  );
}

function RelationshipDirectionSummary({ title, labels, emptyLabel }: { title: string; labels: string[]; emptyLabel: string }) {
  return (
    <section style={{ display: 'grid', gap: 8, padding: 14, borderRadius: 18, background: 'rgba(3, 12, 28, 0.48)', border: '1px solid rgba(180, 223, 255, 0.12)' }}>
      <h3 style={{ margin: 0, fontSize: 14 }}>{title}</h3>
      <p style={{ margin: 0, color: 'rgba(239, 246, 255, 0.78)' }}>{labels.length > 0 ? labels.join('、') : emptyLabel}</p>
    </section>
  );
}

function findLabels(pair: RelationshipPair, sourceNodeId: string, targetNodeId: string): string[] {
  return pair.directions.find((direction) => direction.sourceNodeId === sourceNodeId && direction.targetNodeId === targetNodeId)?.labels ?? [];
}
